import { useCallback, useMemo, useState } from 'react'

import get from 'lodash/get'

import { callApiNew } from 'api/axios'
import { formatCareHospitalInfo } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import {
  CareHospital,
  CareHospitalInfoApi,
  LatLngType,
  LOCALE,
} from 'interfaces/types'

import { DistricItem } from '.'

export function getCommonQueryParams({
  specialties,
}: {
  specialties: string[]
}): ClinicLocatorSearchParams {
  const newParams: ClinicLocatorSearchParams = {
    type: 'hospital',
    allowSearchInDistrict: 1,
    showDistricts: 1,
  }
  if (specialties.length) {
    newParams.specialtyIds = specialties.join(',')
  }
  return newParams
}

export function sanitizeDuplicateClinics(
  clinics?: CareHospital[],
): CareHospital[] {
  if (!clinics || !clinics.length) {
    return []
  }
  const newClinics = [...clinics]
  const uniqueClinics = Array.from(
    new Set(newClinics.map((clinic) => clinic.id)),
  ).map((id) => {
    return newClinics.find((clinic) => clinic.id === id)
  })
  return uniqueClinics.filter(Boolean) as CareHospital[]
}

export function sanitizeDuplicateDistricts(
  districts?: DistricItem[],
): DistricItem[] {
  if (!districts || !districts.length) {
    return []
  }
  const uniqueDistricts = new Map()
  const newDistricts: DistricItem[] = []
  districts.forEach((district) => uniqueDistricts.set(district.id, district))
  uniqueDistricts.forEach((value) => {
    newDistricts.push(value)
  })

  return newDistricts
}

export function formatCareHospitalInfoClinics(infoPath?: string) {
  return (clinic: CareHospitalInfoApi) => {
    const newSearch = { ...clinic }
    if (infoPath) {
      newSearch.name = get(clinic, infoPath, clinic.name)
    }
    return formatCareHospitalInfo(newSearch)
  }
}

export const GMAP_OPTIONS: google.maps.MapOptions = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  styles: [
    {
      featureType: 'all',
      elementType: 'labels.icon',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.medical',
      elementType: 'labels.icon',
      stylers: [{ visibility: 'on' }],
    },
  ],
}

export const GMAP_DEFAULT_ZOOM = 13

export const GMAP_CENTER_LOCALE: Partial<Record<LOCALE, LatLngType>> = {
  [LOCALE.Vietnam]: {
    lat: 10.762622,
    lng: 106.660172,
  },
  [LOCALE.Indonesia]: {
    lat: -6.208763,
    lng: 106.845599,
  },
  [LOCALE.Malaysia]: {
    lat: 3.139003,
    lng: 101.686855,
  },
  [LOCALE.Thailand]: {
    lat: 13.756331,
    lng: 100.501765,
  },
  [LOCALE.Cambodia]: {
    lat: 11.544873,
    lng: 104.892167,
  },
  [LOCALE.Taiwan]: {
    lat: 25.032969,
    lng: 121.565418,
  },
  [LOCALE.Myanmar]: {
    lat: 19.763306,
    lng: 96.07851,
  },
  [LOCALE.PhilippinesEnglish]: {
    lat: 14.599512,
    lng: 120.98422,
  },
  [LOCALE.India]: {
    lat: 28.613939,
    lng: 77.209021,
  },
}

export type ClinicLocatorSearchParams = {
  type: string
  specialtyIds?: string
  showDistricts: number
  latitude?: string | number
  longitude?: string | number
  distance?: number
  districtId?: string
  keyword?: string
  page?: number
  perPage?: number
  allowSearchInDistrict?: number
}

export function useClinics({ infoPath }: { infoPath?: string }) {
  const [loading, setLoading] = useState(false)

  const fetchClinics = useCallback(
    async (params: ClinicLocatorSearchParams) => {
      setLoading(true)
      try {
        const res = await callApiNew(
          PATHS.CARE.GET_SEARCH_CARE,
          { params },
          'careV2',
        )
        const { _data } = res || {}
        if (!_data) {
          throw new Error(JSON.stringify(res))
        }
        const { searches } = _data || {}
        return {
          data: {
            ..._data,
            searches: searches.map(formatCareHospitalInfoClinics(infoPath)), // .filter((i) => i.id !== 4076) // WRONG LAT LNG ON STAGING,
          },
          errors: [],
        }
      } catch (error) {
        return { data: null, errors: [] }
      } finally {
        setLoading(false)
      }
    },
    [infoPath],
  )

  return useMemo(() => ({ loading, setLoading, fetchClinics }), [
    loading,
    setLoading,
    fetchClinics,
  ])
}
